
import { Companies } from "@/api";
import {
  COMPANY_STATUS_ACTIVE,
  COMPANY_STATUS_PROSPECTIVE
} from "@/api/companies";
import { CompaniesWithPagination, Company } from "@/api/types";
import TitleHeader from "@/components/common/TitleHeader.vue";
import { TableOptions } from "@/types";
import moment from "moment";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";

const namespace = "auth";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    TitleHeader
  }
})
export default class CompaniesListPage extends Vue {
  headers = [
    {
      text: "Name",
      value: "name"
    },
    {
      text: "Address",
      value: "address"
    },
    {
      text: "Lead Type",
      value: "typeName"
    },
    {
      text: "Actions",
      value: "controls",
      sortable: false,
      width: "280px"
    }
  ];
  nameFilter = "";
  page = 1;
  options: TableOptions = {} as TableOptions;
  errors = [];

  /** LMS-1224: 0 = Active customers, 1 = Prospective ones. */
  tab = 0;
  exporting = false;
  contractTypes: Array<any> = [];
  activateDialog = false;
  activating = false;
  activateCompany: Company | null = null;
  activateForm: { hansaAcc: string; contractTypeId: number | null } = {
    hansaAcc: "",
    contractTypeId: null
  };
  companies: Array<Company> = [];
  companiesWithPagination: CompaniesWithPagination = {
    meta: {
      perPage: 10
    }
  };
  timeout = 0;

  @Watch("options", { deep: true })
  async onOptionsChange(val: any, old: any) {
    if (old !== val) await this.loadCompaniesData(val.page);
  }

  @Watch("tab")
  async onTabChange() {
    this.options.page = 1;
    await this.loadCompaniesData(1);
  }

  @Watch("nameFilter")
  async onFilterNameChange() {
    window.clearTimeout(this.timeout);
    this.timeout = window.setTimeout(async () => {
      // if (val.length >= 3) {
      await this.loadCompaniesData(1);
      // } else {
      // await this.loadCompaniesData(1);
      // }
      this.options.page = 1;
    }, 500);
  }

  async created() {
    try {
      this.contractTypes = await Companies.contractTypes();
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }

  /** LMS-1224 */
  get isProspectiveTab(): boolean {
    return this.tab === 1;
  }

  get companyStatus(): number {
    return this.isProspectiveTab
      ? COMPANY_STATUS_PROSPECTIVE
      : COMPANY_STATUS_ACTIVE;
  }

  hasPermission(name: string): boolean {
    const currentUser: any = this.getUser;
    return currentUser && currentUser.permissions
      ? Boolean(
          currentUser.permissions.find(
            (permission: string) => permission === name
          )
        )
      : false;
  }

  get canShowProspective(): boolean {
    return this.hasPermission("show_prospective_company");
  }

  get canActivate(): boolean {
    return this.hasPermission("activate_prospective_company");
  }

  async loadCompaniesData(page = 1) {
    try {
      const companiesWithPagination = await Companies.list(
        page,
        this.nameFilter,
        this.companyStatus
      );
      this.companies = companiesWithPagination.data
        ? companiesWithPagination.data
        : [];
      this.companiesWithPagination = companiesWithPagination;
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }

  async onCreate() {
    await this.$router.push({ name: "CompaniesCreate" });
  }

  @Getter("getUser", { namespace })
  getUser!: object | null;

  get canExport(): boolean {
    return this.hasPermission("export_company");
  }

  async onExport() {
    this.exporting = true;
    try {
      const prefix = this.isProspectiveTab
        ? "prospective-customers"
        : "companies";
      const fileName = `${prefix}-${moment().format("DD/MM/YYYY")}.xlsx`;
      const data = await Companies.exportCompanies(
        this.nameFilter,
        this.companyStatus
      );
      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    } finally {
      this.exporting = false;
    }
  }

  async onDelete(id: number) {
    const res = await this.$dialog.error({
      text: "Do you really want to delete item?",
      title: "Warning",
      persistent: false,
      actions: {
        No: { text: "No" },
        Yes: {
          color: "red",
          text: "Yes I do"
        }
      }
    });
    if (res === "Yes") {
      try {
        await Companies.remove(id.toString());
        await this.loadCompaniesData(this.page);
        this.nameFilter = "";
        await this.$success(
          "<strong>Success!</strong> Company has been deleted!"
        );
      } catch (e) {
        const err = e as any;
        await this.$error(err.errors);
      }
    }
  }

  async onEdit(id: number, companyType: string) {
    await this.$router.push({
      name: "CompaniesEdit",
      params: {
        id: id.toString(),
        companyType: companyType
      }
    });
  }

  async onView(id: number) {
    await this.$router.push({
      name: "CompaniesView",
      params: { id: id.toString() }
    });
  }

  /**
   * Activation of a prospective customer (LMS-1224).
   *
   * Hansa Acc is the number that makes a customer real, so it is asked for
   * here; when it turns out to be taken the user is offered a merge into the
   * customer already holding it.
   */
  openActivate(company: Company) {
    this.activateCompany = company;
    this.activateForm = {
      hansaAcc: "",
      contractTypeId: (company as any).contractTypeId || null
    };
    this.activateDialog = true;
  }

  closeActivate() {
    this.activateDialog = false;
    this.activateCompany = null;
  }

  async submitActivate() {
    if (!this.activateCompany) {
      return;
    }

    this.activating = true;

    try {
      const company: any = this.activateCompany;
      const result = await Companies.activate(String(company.id), {
        hansaAcc: this.activateForm.hansaAcc,
        contractTypeId: this.activateForm.contractTypeId,
        name: company.name,
        address: company.address,
        regNumber: company.regNumber,
        vat: company.vat
      });

      if (result.activated) {
        this.activateDialog = false;
        this.activateCompany = null;
        await this.loadCompaniesData(this.page);
        await this.$success(
          "<strong>Success!</strong> The customer has been activated!"
        );
        return;
      }

      await this.confirmMerge(String(company.id), result);
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors || { err: "Something went wrong" });
    } finally {
      this.activating = false;
    }
  }

  async confirmMerge(companyId: string, result: any) {
    const res = await this.$dialog.warning({
      text: result.message,
      title: "Warning",
      persistent: true,
      actions: {
        Cancel: { text: "Cancel" },
        Yes: { color: "red", text: "Yes" }
      }
    });

    if (res !== "Yes") {
      return;
    }

    try {
      await Companies.merge(companyId, result.conflict.id);
      this.activateDialog = false;
      this.activateCompany = null;
      await this.loadCompaniesData(this.page);
      await this.$success(
        "<strong>Success!</strong> The customers have been merged!"
      );
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors || { err: "Something went wrong" });
    }
  }
}
